import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { FormInputDraft, serializeFormInputs } from '@/lib/form-input-draft';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  isArrayOfEditablePrimitiveItems,
  isSchemaRecord,
  SchemaJsonArrayEditor,
  SchemaJsonObjectEditor,
  SchemaPrimitiveArrayInput,
  SchemaNumberInput,
} from '@/components/schema-value-editors';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ConnectorType } from '@/lib/api';

// ============================================================
// Schema Parsing Types
// ============================================================

type FieldType =
  | 'text'
  | 'password'
  | 'number'
  | 'boolean'
  | 'select'
  | 'multi-select'
  | 'array'
  | 'json-array'
  | 'object'
  | 'object-array';

type SelectOptionValue = string | number | boolean;

interface SelectOption {
  id: string;
  value: SelectOptionValue;
  label: string;
}

interface FieldConfig {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  description?: string;
  placeholder?: string;
  defaultValue?: unknown;
  example?: unknown;
  options?: SelectOption[];
  schema: Record<string, unknown>;
  itemSchema?: Record<string, unknown>;
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    minItems?: number;
    maxItems?: number;
  };
}

type DescriptionPart =
  | { id: string; type: 'text'; value: string }
  | { id: string; type: 'link'; href: string; label: string };

function parseAnchoredText(input: string): DescriptionPart[] {
  const parts: DescriptionPart[] = [];
  const anchorRegex = /<a\b[^>]*href=['"]([^'"]+)['"][^>]*>(.*?)<\/a>/gi;
  let cursor = 0;
  let match: RegExpExecArray | null = anchorRegex.exec(input);

  while (match !== null) {
    const [fullMatch, href, label] = match;
    const start = match.index;

    if (start > cursor) {
      parts.push({
        id: `text-${cursor}-${start}`,
        type: 'text',
        value: input.slice(cursor, start),
      });
    }

    parts.push({
      id: `link-${start}-${start + fullMatch.length}`,
      type: 'link',
      href: href.trim(),
      label: label.replace(/<[^>]*>/g, '').trim() || href.trim(),
    });

    cursor = start + fullMatch.length;
    match = anchorRegex.exec(input);
  }

  if (cursor < input.length) {
    parts.push({
      id: `text-${cursor}-${input.length}`,
      type: 'text',
      value: input.slice(cursor),
    });
  }

  return parts.length > 0 ? parts : [{ id: `text-0-${input.length}`, type: 'text', value: input }];
}

function DescriptionText({
  text,
  onOpenLink,
}: {
  text: string;
  onOpenLink?: (href: string) => void;
}) {
  const parts = parseAnchoredText(text);

  return (
    <p className="text-xs text-muted-foreground">
      {parts.map((part) =>
        part.type === 'text' ? (
          <span key={part.id}>{part.value}</span>
        ) : onOpenLink ? (
          <button
            key={part.id}
            type="button"
            className="text-blue-600 hover:underline"
            onClick={() => onOpenLink(part.href)}
          >
            {part.label}
          </button>
        ) : (
          <a
            key={part.id}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {part.label}
          </a>
        )
      )}
    </p>
  );
}

// ============================================================
// Schema Parsing Functions
// ============================================================

function formatLabel(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function extractLabelFromDescription(description: string | undefined, fieldName: string): string {
  if (!description) return formatLabel(fieldName);
  // Strip a trailing parenthetical example. The comma after "e.g." is optional:
  // matching only "(e.g., …)" left "(e.g. …)" in place, and the sentence split
  // below then cut at the period inside "e.g." — rendering a Gmail field as
  // `Gmail label to sync (e`.
  const label = description.replace(/\s*\(e\.g\.,?\s.*?\)/gis, '').trim();
  // Take the first sentence, but ignore periods that are part of an
  // abbreviation ("e.g.", "i.e.") or a decimal rather than a sentence end.
  const firstSentence = label
    .split(/(?<![A-Za-z0-9])\.(?=\s|$)|\.(?=\s+[A-Z])/)[0]
    .trim()
    // Stripping a trailing "(e.g. …)" leaves the sentence period dangling.
    .replace(/\.$/, '');
  return firstSentence || label || formatLabel(fieldName);
}

function formatExampleValue(example: unknown): string | undefined {
  if (example === undefined || example === null) return undefined;
  if (typeof example === 'string' || typeof example === 'number' || typeof example === 'boolean') {
    return String(example);
  }
  if (Array.isArray(example)) {
    const parts = example
      .map((item) =>
        typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
          ? String(item)
          : null
      )
      .filter((item): item is string => item !== null);
    if (parts.length > 0) return parts.join(', ');
  }
  try {
    return JSON.stringify(example);
  } catch {
    return undefined;
  }
}

function buildSelectOptions(values: SelectOptionValue[]): SelectOption[] {
  const labels = values.map((value) => formatLabel(String(value)));
  const labelTypes = new Map<string, Set<string>>();
  labels.forEach((label, index) => {
    const types = labelTypes.get(label) ?? new Set<string>();
    types.add(typeof values[index]);
    labelTypes.set(label, types);
  });

  return values.map((value, index) => {
    const label = labels[index];
    const distinguishesTypes = (labelTypes.get(label)?.size ?? 0) > 1;
    return {
      id: `option-${index}`,
      value,
      label: distinguishesTypes
        ? `${label} (${typeof value === 'string' ? 'text' : typeof value})`
        : label,
    };
  });
}

function parseField(
  name: string,
  schema: Record<string, unknown>,
  isRequired: boolean
): FieldConfig | null {
  // Handle Optional wrapper (TypeBox style)
  const anyOf = schema.anyOf as Array<Record<string, unknown>> | undefined;
  if (anyOf && anyOf.length > 0) {
    const nullOption = anyOf.find((opt) => opt.type === 'null');
    if (nullOption) {
      const nonNullOptions = anyOf.filter((opt) => opt.type !== 'null');
      if (nonNullOptions.length === 1) {
        const wrapperAnnotations = Object.fromEntries(
          Object.entries(schema).filter(([key]) => key !== 'anyOf')
        );
        return parseField(
          name,
          { ...nonNullOptions[0], ...wrapperAnnotations },
          isRequired
        );
      }
    }
  }

  const config: FieldConfig = {
    name,
    label:
      (schema.title as string | undefined) ||
      extractLabelFromDescription(schema.description as string | undefined, name),
    type: 'text',
    required: isRequired,
    description: schema.description as string | undefined,
    defaultValue: schema.default,
    example: schema.example,
    schema,
  };
  const examplePlaceholder = formatExampleValue(schema.example);

  // Determine field type
  if (schema.type === 'object' || isSchemaRecord(schema.properties)) {
    config.type = 'object';
  } else if (schema.type === 'string') {
    config.type = schema.format === 'password' ? 'password' : 'text';
    config.placeholder = examplePlaceholder || `Enter ${formatLabel(name).toLowerCase()}`;
    config.validation = {
      minLength: schema.minLength as number | undefined,
      maxLength: schema.maxLength as number | undefined,
    };
  } else if (schema.type === 'number' || schema.type === 'integer') {
    config.type = 'number';
    config.placeholder = examplePlaceholder;
    config.validation = {
      min: schema.minimum as number | undefined,
      max: schema.maximum as number | undefined,
    };
  } else if (schema.type === 'boolean') {
    config.type = 'boolean';
  } else if (schema.type === 'array') {
    const items = schema.items as Record<string, unknown> | undefined;
    config.itemSchema = items;
    if (items?.type === 'object' || items?.properties) {
      config.type = 'object-array';
    } else if (items?.enum && Array.isArray(items.enum)) {
      config.type = 'multi-select';
      config.options = buildSelectOptions(items.enum as SelectOptionValue[]);
    } else if (items?.anyOf || items?.oneOf) {
      const unionOptions = (items.anyOf || items.oneOf) as Array<Record<string, unknown>>;
      const literalValues = unionOptions
        .filter((opt) => opt.const !== undefined)
        .map((opt) => opt.const as string | number);
      if (literalValues.length > 0 && literalValues.length === unionOptions.length) {
        config.type = 'multi-select';
        config.options = buildSelectOptions(literalValues as SelectOptionValue[]);
      } else if (
        items?.type === 'string' ||
        items?.type === 'number' ||
        items?.type === 'integer' ||
        items?.type === 'boolean'
      ) {
        config.type = 'array';
        config.placeholder = examplePlaceholder ?? 'Add items...';
      } else {
        config.type = 'json-array';
      }
    } else if (
      items?.type === 'string' ||
      items?.type === 'number' ||
      items?.type === 'integer' ||
      items?.type === 'boolean'
    ) {
      config.type = 'array';
      config.placeholder = examplePlaceholder ?? 'Add items...';
    } else {
      config.type = 'json-array';
    }
    config.validation = {
      minItems: schema.minItems as number | undefined,
      maxItems: schema.maxItems as number | undefined,
    };
  } else if (anyOf) {
    // Union types -> select dropdown
    const literalValues = anyOf
      .filter((opt) => opt.const !== undefined)
      .map((opt) => opt.const as string | number);
    if (literalValues.length > 0) {
      config.type = 'select';
      config.options = buildSelectOptions(literalValues as SelectOptionValue[]);
      config.placeholder = examplePlaceholder;
    }
  }

  // Check for enum
  if (schema.enum && Array.isArray(schema.enum)) {
    config.type = 'select';
    config.options = buildSelectOptions(schema.enum as SelectOptionValue[]);
    config.placeholder = examplePlaceholder;
  }

  return config;
}

function parseSchema(schema: Record<string, unknown> | undefined): FieldConfig[] {
  if (!schema?.properties) return [];

  const fields: FieldConfig[] = [];
  const properties = schema.properties as Record<string, Record<string, unknown>>;
  const required = (schema.required as string[]) || [];

  for (const [fieldName, fieldSchema] of Object.entries(properties)) {
    const field = parseField(fieldName, fieldSchema, required.includes(fieldName));
    if (field) fields.push(field);
  }

  return fields;
}

function ObjectField({
  id,
  label,
  value,
  schema,
  readOnly,
  onOpenLink,
  onChange,
}: {
  id: string;
  label: string;
  value: unknown;
  schema: Record<string, unknown>;
  readOnly: boolean;
  onOpenLink?: (href: string) => void;
  onChange: (value: Record<string, unknown> | FormInputDraft) => void;
}) {
  const properties = isSchemaRecord(schema.properties)
    ? (schema.properties as Record<string, Record<string, unknown>>)
    : null;
  const currentValue = isSchemaRecord(value) ? value : {};

  if (
    !properties ||
    Object.keys(properties).length === 0 ||
    (!isSchemaRecord(value) && value !== undefined && value !== null)
  ) {
    return (
      <SchemaJsonObjectEditor
        id={id}
        value={value}
        ariaLabel={`${label} JSON object`}
        readOnly={readOnly}
        onChange={onChange}
      />
    );
  }

  const declaredKeys = new Set(Object.keys(properties));
  return (
    <fieldset aria-label={label} className="rounded-md border p-3">
      <DynamicConnectorForm
        schema={{ type: 'object', properties, required: schema.required }}
        values={currentValue}
        onValuesChange={(nextValues) => {
          const undeclaredValues = Object.fromEntries(
            Object.entries(currentValue).filter(([key]) => !declaredKeys.has(key))
          );
          onChange({ ...undeclaredValues, ...nextValues });
        }}
        onOpenLink={onOpenLink}
        fieldIdPrefix={`${id}-`}
        readOnly={readOnly}
      />
    </fieldset>
  );
}

function ObjectArrayField({
  id,
  label,
  value,
  itemSchema,
  validation,
  readOnly,
  onOpenLink,
  onChange,
}: {
  id: string;
  label: string;
  value: unknown;
  itemSchema?: Record<string, unknown>;
  validation?: FieldConfig['validation'];
  readOnly: boolean;
  onOpenLink?: (href: string) => void;
  onChange: (value: unknown[] | FormInputDraft) => void;
}) {
  if (!Array.isArray(value) && value !== undefined && value !== null) {
    return (
      <SchemaJsonArrayEditor
        id={id}
        value={value}
        ariaLabel={`${label} JSON array`}
        readOnly={readOnly}
        itemType="object"
        minItems={validation?.minItems}
        maxItems={validation?.maxItems}
        onChange={onChange}
      />
    );
  }

  const rows = Array.isArray(value) ? value : [];
  const properties = isSchemaRecord(itemSchema?.properties)
    ? (itemSchema.properties as Record<string, Record<string, unknown>>)
    : null;
  const declaredKeys = new Set(Object.keys(properties ?? {}));
  const canAdd = validation?.maxItems === undefined || rows.length < validation.maxItems;
  const canRemove = validation?.minItems === undefined || rows.length > validation.minItems;

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">No rows</p>
      ) : (
        rows.map((row, index) => {
          const rowId = `${id}-${index}`;
          return (
            // Schema arrays have no stable row identity. Every control below is
            // fully controlled from the indexed row, so preserving the index is
            // preferable to remounting the row on every edit.
            // biome-ignore lint/suspicious/noArrayIndexKey: schema rows have no stable id
            <fieldset key={index} className="rounded-md border p-3">
              <legend className="px-1 text-xs font-medium text-muted-foreground">
                {label} row {index + 1}
              </legend>
              {properties && Object.keys(properties).length > 0 && isSchemaRecord(row) ? (
                <DynamicConnectorForm
                  schema={{
                    type: 'object',
                    properties,
                    required: itemSchema?.required,
                  }}
                  values={row}
                  onValuesChange={(nextValues) => {
                    const undeclaredValues = Object.fromEntries(
                      Object.entries(row).filter(([key]) => !declaredKeys.has(key))
                    );
                    const nextRows = [...rows];
                    nextRows[index] = { ...undeclaredValues, ...nextValues };
                    onChange(nextRows);
                  }}
                  onOpenLink={onOpenLink}
                  fieldIdPrefix={`${rowId}-`}
                  readOnly={readOnly}
                />
              ) : (
                <SchemaJsonObjectEditor
                  id={`${rowId}-json`}
                  value={row}
                  ariaLabel={`${label} row ${index + 1} JSON object`}
                  readOnly={readOnly}
                  onChange={(nextValue) => {
                    const nextRows = [...rows];
                    nextRows[index] = nextValue;
                    onChange(nextRows);
                  }}
                />
              )}
              {readOnly ? null : (
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${label} row ${index + 1}`}
                    disabled={!canRemove}
                    onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    Remove
                  </Button>
                </div>
              )}
            </fieldset>
          );
        })
      )}
      {readOnly ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={`Add ${label} row`}
          disabled={!canAdd}
          onClick={() => onChange([...rows, createConnectorDraft(itemSchema)])}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add row
        </Button>
      )}
    </div>
  );
}

// ============================================================
// Component
// ============================================================

export interface DynamicConnectorFormBaseProps {
  connectorType?: ConnectorType;
  schema?: Record<string, unknown>;
  values: Record<string, unknown>;
  /** Route description links through a containing host instead of raw anchors. */
  onOpenLink?: (href: string) => void;
  fieldIdPrefix?: string;
  readOnly?: boolean;
  /** Use browser-native menus where portaled popovers can be clipped by a host iframe. */
  nativeSelects?: boolean;
}

/** The form holds no state, so an editable one is unusable without a sink for
 *  its edits: only a read-only view may leave `onValuesChange` off. */
type DynamicConnectorFormProps = DynamicConnectorFormBaseProps &
  (
    | { readOnly: true; onValuesChange?: never }
    | { onValuesChange: (values: Record<string, unknown>) => void }
  );

/** Apply defaults at the form owner's initialization boundary, never on edits. */
export function createConnectorDraft(
  schema: Record<string, unknown> | undefined | null,
  initial: Record<string, unknown> = {},
  examples = false,
): Record<string, unknown> {
  const values = { ...initial };
  for (const field of parseSchema(schema ?? undefined)) {
    if (!(field.name in values)) {
      if (examples && field.example !== undefined) values[field.name] = field.example;
      else if (field.defaultValue !== undefined) values[field.name] = field.defaultValue;
      else if (field.type === 'boolean') values[field.name] = false;
      else if (field.type === 'object' && isSchemaRecord(field.schema.properties))
        values[field.name] = {};
    }
    const value = values[field.name];
    if (field.type === 'object' && isSchemaRecord(value)) {
      values[field.name] =
        isSchemaRecord(field.schema.properties) && Object.keys(field.schema.properties).length > 0
          ? createConnectorDraft(field.schema, value, examples)
          : new FormInputDraft('json-object', JSON.stringify(value, null, 2), value);
    } else if (field.type === 'object-array' && Array.isArray(value)) {
      values[field.name] = value.map((row) =>
        isSchemaRecord(row) ? createConnectorDraft(field.itemSchema, row, examples) : row,
      );
    }
  }
  return values;
}

/** Submission boundary only: retain explicit empty arrays, omit unset fields. */
export function cleanConnectorValues(values: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, draft] of Object.entries(values)) {
    if (draft instanceof FormInputDraft) {
      const value = serializeFormInputs(draft);
      if (value !== '' && value !== undefined && value !== null) cleaned[key] = value;
    } else if (Array.isArray(draft)) {
      cleaned[key] = draft.map((item) =>
        item instanceof FormInputDraft
          ? serializeFormInputs(item)
          : isSchemaRecord(item)
            ? cleanConnectorValues(item)
            : item,
      );
    } else if (isSchemaRecord(draft)) {
      cleaned[key] = cleanConnectorValues(draft);
    } else if (draft !== '' && draft !== undefined && draft !== null) {
      cleaned[key] = draft;
    }
  }
  return cleaned;
}

export function DynamicConnectorForm({
  connectorType,
  schema,
  values: formValues,
  onValuesChange,
  onOpenLink,
  fieldIdPrefix = '',
  readOnly = false,
  nativeSelects = false,
}: DynamicConnectorFormProps) {
  // Parse schema to get fields
  const fields = useMemo(
    () => parseSchema(schema ?? connectorType?.options_schema),
    [schema, connectorType],
  );

  const updateValue = (name: string, value: unknown) => {
    onValuesChange?.({ ...formValues, [name]: value });
  };

  if (fields.length === 0) {
    return <p className="text-sm text-muted-foreground">No configuration required.</p>;
  }

  return (
    <div className="space-y-4">
      {fields.map((field) => {
        const fieldId = `${fieldIdPrefix}${field.name}`;
        return (
          <div key={field.name} className="space-y-1.5">
            {field.type === 'object' || field.type === 'object-array' ? (
              <p className="text-sm font-medium">
                {field.label}
                {field.required && !readOnly && <span className="text-red-500 ml-1">*</span>}
              </p>
            ) : (
              <label htmlFor={fieldId} className="text-sm font-medium">
                {field.label}
                {field.required && !readOnly && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {field.description && (
              <DescriptionText text={field.description} onOpenLink={onOpenLink} />
            )}

            {(field.type === 'text' || field.type === 'password') && (
              <Input
                id={fieldId}
                type={field.type}
                value={(formValues[field.name] as string | number) ?? ''}
                onChange={(e) => updateValue(field.name, e.target.value)}
                placeholder={field.placeholder}
                readOnly={readOnly}
                disabled={readOnly}
              />
            )}

            {field.type === 'number' && (
              <SchemaNumberInput
                id={fieldId}
                value={formValues[field.name]}
                integer={field.schema.type === 'integer'}
                onChange={(value) => updateValue(field.name, value)}
                placeholder={field.placeholder}
                readOnly={readOnly}
              />
            )}

            {field.type === 'boolean' && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={fieldId}
                  checked={(formValues[field.name] as boolean) ?? false}
                  onCheckedChange={(checked) => updateValue(field.name, checked)}
                  disabled={readOnly}
                />
                <label htmlFor={fieldId} className="text-sm">
                  Enable
                </label>
              </div>
            )}

            {field.type === 'select' && field.options && nativeSelects && (
              <select
                id={fieldId}
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm  outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                value={
                  field.options.find((option) => Object.is(option.value, formValues[field.name]))
                    ?.id ?? ''
                }
                onChange={(event) => {
                  const option = field.options?.find(
                    (candidate) => candidate.id === event.target.value
                  );
                  if (option) updateValue(field.name, option.value);
                }}
                required={field.required}
                disabled={readOnly}
              >
                <option value="" disabled>
                  {field.placeholder || `Select ${field.label.toLowerCase()}...`}
                </option>
                {field.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {field.type === 'select' && field.options && !nativeSelects && (
              <Select
                value={
                  field.options.find((option) => Object.is(option.value, formValues[field.name]))
                    ?.id ?? ''
                }
                onValueChange={(value) => {
                  const option = field.options?.find((candidate) => candidate.id === value);
                  if (option) updateValue(field.name, option.value);
                }}
                disabled={readOnly}
              >
                <SelectTrigger id={fieldId} aria-required={field.required || undefined}>
                  <SelectValue
                    placeholder={field.placeholder || `Select ${field.label.toLowerCase()}...`}
                  />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {field.type === 'multi-select' && field.options && (
              <div className="flex flex-wrap gap-2 p-2 border rounded-md">
                {field.options.map((option) => {
                  const selected = Array.isArray(formValues[field.name])
                    ? (formValues[field.name] as SelectOptionValue[]).some((value) =>
                        Object.is(value, option.value)
                      )
                    : false;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={readOnly}
                      onClick={() => {
                        const current = (formValues[field.name] as SelectOptionValue[]) || [];
                        if (selected) {
                          updateValue(
                            field.name,
                            current.filter((value) => !Object.is(value, option.value))
                          );
                        } else {
                          updateValue(field.name, [...current, option.value]);
                        }
                      }}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        selected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background hover:bg-muted border-border'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}

            {field.type === 'array' &&
              (formValues[field.name] !== undefined &&
              formValues[field.name] !== null &&
              !isArrayOfEditablePrimitiveItems(formValues[field.name]) ? (
                <SchemaJsonArrayEditor
                  id={fieldId}
                  value={formValues[field.name]}
                  ariaLabel={`${field.label} JSON array`}
                  readOnly={readOnly}
                  itemType={field.itemSchema?.type}
                  itemEnum={field.itemSchema?.enum as unknown[] | undefined}
                  minItems={field.validation?.minItems}
                  maxItems={field.validation?.maxItems}
                  onChange={(value) => updateValue(field.name, value)}
                />
              ) : (
                <SchemaPrimitiveArrayInput
                  id={fieldId}
                  value={formValues[field.name]}
                  itemType={field.itemSchema?.type}
                  itemEnum={field.itemSchema?.enum as unknown[] | undefined}
                  minItems={field.validation?.minItems}
                  maxItems={field.validation?.maxItems}
                  placeholder={field.placeholder}
                  readOnly={readOnly}
                  onChange={(value) => updateValue(field.name, value)}
                />
              ))}

            {field.type === 'json-array' && (
              <SchemaJsonArrayEditor
                id={fieldId}
                value={formValues[field.name]}
                ariaLabel={`${field.label} JSON array`}
                readOnly={readOnly}
                itemType={field.itemSchema?.type}
                itemEnum={field.itemSchema?.enum as unknown[] | undefined}
                minItems={field.validation?.minItems}
                maxItems={field.validation?.maxItems}
                onChange={(value) => updateValue(field.name, value)}
              />
            )}

            {field.type === 'object' && (
              <ObjectField
                id={fieldId}
                label={field.label}
                value={formValues[field.name]}
                schema={field.schema}
                readOnly={readOnly}
                onOpenLink={onOpenLink}
                onChange={(value) => updateValue(field.name, value)}
              />
            )}

            {field.type === 'object-array' && (
              <ObjectArrayField
                id={fieldId}
                label={field.label}
                value={formValues[field.name]}
                itemSchema={field.itemSchema}
                validation={field.validation}
                readOnly={readOnly}
                onOpenLink={onOpenLink}
                onChange={(value) => updateValue(field.name, value)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
